import { useState } from "react";
import UiTable from "../../../components/table/Table.component";
import { TableMessages } from "../../../models/components/table/table-message.model";
import {
	FieldMessageSeverity,
	UiFieldMessageProps,
} from "../../../models/components/field-message.model";
import { TablePagination } from "../../../models/components/table/table-pagination.model";
import { TableColumnTemplate } from "../../../models/components/table/table-column.model";
import { TableStyle } from "../../../models/components/table/table.model";
import { usePostDischarge } from "../dashboard/post-discharge/usePostDischarge";
import { BiometricsTemplate } from "../../../services/biometrics/biometrics-template.service";
import { PostDischargeRow } from "../../../models/pages/post-discharge-modal/post-discharge-modal";
import { PostDischargeTemplate } from "../../../services/post-discharge/post-discharge-template.service";
import PostDischargeDelete from "./PostDischargeDelete";
import { Form } from "react-final-form";
import { PostDischargeInlineUpdateForm } from "../../../models/pages/post-discharge-modal/post-discharge-update.model";
import { PostDischargeProcedureType, ProcedureStatus } from "../../../models/procedures.model";
import { EndpointsService } from "../../../services/endpoints/endpoints.service";
import { Formatter } from "../../../services/formatter/formatter.service";
import UiInputDate from "../../../components/input-date/InputDate.component";
import { Validations } from "../../../services/form/validations.service";

const PostDischargeList = ({
	participantId,
	postDischargeQuery,
	postDischargeInfoQuery,
}: {
	participantId: number;
	postDischargeQuery: any;
	postDischargeInfoQuery: any;
}) => {
	/* Table */
	const [editingRow, setEditingRow] = useState<PostDischargeRow | null>(null);
	const [deletingRow, setDeletingRow] = useState<PostDischargeRow | null>(null);
	const [modalIsVisible, setModalIsVisible] = useState<boolean>(false);

	const initialForm = {
		notes: editingRow ? editingRow.notes || "" : "",
		type: editingRow ? editingRow.type : PostDischargeProcedureType.DISCHARGED_FROM_HOSPITAL,
		status: editingRow ? editingRow.status : ProcedureStatus.SCHEDULED,
		dateOn: editingRow ? (editingRow.dateOn ? new Date(editingRow.dateOn) : null) : null,
	};
	const [updateForm, setUpdateForm] = useState<PostDischargeInlineUpdateForm>(initialForm);

	const savePostDischarge = async (
		row: PostDischargeRow,
		updateForm: PostDischargeInlineUpdateForm
	) => {
		await EndpointsService.dataRetriever.updatePostDischarge({
			body: {
				...row,
				...updateForm,
				type: updateForm.type!,
				status: updateForm.status!,
				notes: updateForm.notes,
				dateOn: updateForm.dateOn!.toUTCString(),
			},
		});
	};

	const onSubmitEdit = async () => {
		// TODO: handle validation and server errors
		await savePostDischarge(editingRow!, updateForm);
		setEditingRow(null);
		postDischargeQuery.invalidate();
		postDischargeInfoQuery.invalidate();
		setUpdateForm({
			status: undefined,
			notes: null,
			type: undefined,
			dateOn: null,
		});
	};

	const onDelete = (e: PostDischargeRow) => {
		setDeletingRow(e);
		setModalIsVisible(true);
	};

	// Pagination
	const [pagination, setPagination] = useState<TablePagination>({
		first: 0,
		rows: 10,
		page: 0,
		sortField: "dateOn",
		sortOrder: -1,
		search: undefined,
		filters: undefined,
	});

	// Messages
	const messages = new TableMessages();
	const [message, setMessage] = useState<UiFieldMessageProps>({
		severity: FieldMessageSeverity.INFO,
		label: messages.empty,
	});

	const mapRows = (query: any): PostDischargeRow[] => {
		let data = query.data;
		if (editingRow) {
			data = data.map((item: PostDischargeRow) => {
				if (item.id === editingRow.id) {
					return { ...item, editing: true };
				}
				return item;
			});
		} else {
			data = data.map((item: PostDischargeRow) => {
				return { ...item, editing: false };
			});
		}
		return data;
	};

	const proceduresQuery = mapRows(usePostDischarge(participantId));

	return (
		<>
			<Form
				onSubmit={onSubmitEdit}
				initialValues={{
					dateOn: updateForm.dateOn,
					status: updateForm.status,
					notes: updateForm.notes,
					type: updateForm.type,
				}}
				render={({ handleSubmit, submitting }) => (
					<form onSubmit={handleSubmit}>
						<UiTable
							dataKey="id"
							/**
							 * PrimeReact memoizes body cells on `rowData` (see
							 * `BodyCell`'s `selectiveCompare`), so a row whose only
							 * change is the `editing` flag can be skipped entirely and
							 * the row never swaps into its edit inputs. Opt out here —
							 * this table is a short, paginated modal list, and the same
							 * opt-out is already used by `AlertValuesTable`.
							 */
							cellMemo={false}
							useAsCard={false}
							type={TableStyle.GREEN}
							columns={[
								{
									field: "dateOn",
									title: "PARTICIPANT.POST_DISCHARGE.MODAL.dateOn",
									sortable: true,
									template: TableColumnTemplate.CUSTOM,
									templateOptions: {
										customTemplate: (row: PostDischargeRow) => {
											if (!row.editing) {
												return (
													<span className="text">
														{
															Formatter.date(row?.dateOn, {
																day: "2-digit",
																month: "2-digit",
																year: "2-digit",
																hour: "2-digit",
																minute: "2-digit",
															}) as string
														}
													</span>
												);
											}

											return (
												<UiInputDate
													id="dateOn"
													placeholder="ENUMS.BIOMETRICS.dateOn"
													name="dateOn"
													removeBottomSpacer={true}
													onChange={(e) =>
														setUpdateForm({
															...updateForm,
															dateOn: e.target.value as Date,
														})
													}
													validations={[Validations.required]}
													value={updateForm.dateOn ?? undefined}
													disabled={submitting}
													showTime
													showButtonBar={false}
												/>
											);
										},
									},
								},
								{
									field: "status",
									title: "PARTICIPANT.POST_DISCHARGE.MODAL.status",
									sortable: true,
									template: TableColumnTemplate.CUSTOM,
									templateOptions: {
										customTemplate: (row: PostDischargeRow) =>
											PostDischargeTemplate.templateStatus(
												row,
												(value) => {
													setUpdateForm({
														...updateForm,
														status: value,
													});
												},
												!!submitting
											),
									},
								},
								{
									field: "type",
									title: "PARTICIPANT.POST_DISCHARGE.MODAL.type",
									sortable: true,
									template: TableColumnTemplate.CUSTOM,
									templateOptions: {
										customTemplate: (row: PostDischargeRow) =>
											PostDischargeTemplate.templatePostDischargeType(
												row,
												(value) => {
													setUpdateForm({
														...updateForm,
														type: value,
													});
												},
												!!submitting
											),
									},
								},
								{
									field: "notes",
									title: "PARTICIPANT.POST_DISCHARGE.MODAL.reason",
									sortable: true,
									template: TableColumnTemplate.CUSTOM,
									templateOptions: {
										customTemplate: (row: PostDischargeRow) =>
											PostDischargeTemplate.templateNotes(
												row,
												(value) => {
													setUpdateForm({
														...updateForm,
														notes: value,
													});
												},
												!!submitting
											),
									},
								},
								{
									field: "edit",
									sortable: false,
									alignment: "right",
									style: { width: BiometricsTemplate.columnsWidth.edit },
									template: TableColumnTemplate.CUSTOM,
									templateOptions: {
										customTemplate: (row: PostDischargeRow) =>
											PostDischargeTemplate.templateActions(
												row,
												(row) => {
													setEditingRow({ ...row, editing: true });
													setUpdateForm({
														notes: row.notes,
														type: row.type,
														status: row.status,
														dateOn: row.dateOn
															? new Date(row.dateOn)
															: null,
													});
												},
												() => {
													setEditingRow(null);
													setUpdateForm(initialForm);
													postDischargeQuery.invalidate();
													postDischargeInfoQuery.invalidate();
												},
												!!submitting,
												onDelete
											),
									},
								},
							]}
							value={proceduresQuery}
							message={message}
							pagination={pagination}
							paginationFn={(event: TablePagination) => {
								const newPagination: TablePagination = {
									...pagination,
									page: event.page,
									rows: event.rows,
									sortField: event.sortField,
									sortOrder: event.sortOrder,
									first: event.first,
								};
								setPagination(newPagination);
							}}
						/>
					</form>
				)}
			/>

			{modalIsVisible && deletingRow && (
				<PostDischargeDelete
					isVisible={modalIsVisible}
					row={deletingRow}
					onComplete={() => {
						setDeletingRow(null);
						setModalIsVisible(false);
						postDischargeQuery.invalidate();
						postDischargeInfoQuery.invalidate();
					}}
					onClose={() => {
						setDeletingRow(null);
						setModalIsVisible(false);
					}}
				/>
			)}
		</>
	);
};

export default PostDischargeList;
